import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTABlock } from "@/components/CTABlock";
import { OFFERS } from "@/data/offers";
import { OFFERS_EN } from "@/data/offers.en";
import { SITE } from "@/constants/site";
import { useLang } from "@/i18n/LangContext";

const Strong = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? (
      <strong key={i} className="font-semibold text-ink">{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );

export default function Services() {
  const { t, l, lang } = useLang();
  const offers = lang === "en" ? OFFERS_EN : OFFERS;
  const s = t.servicesPage;

  return (
    <>
      <Seo
        title={s.seo.title}
        description={s.seo.description}
        path="/services"
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: s.breadcrumb, path: "/services" },
          ]),
        ]}
      />

      <section className="px-5 md:px-10 pt-36 md:pt-44 pb-16 md:pb-24" data-testid="services-hero">
        <div className="mx-auto max-w-7xl">
          <Breadcrumb items={[{ label: t.common.home, to: "/" }, { label: s.breadcrumb }]} />
          <div className="max-w-3xl">
            <SectionTag>{s.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h1 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-balance">
                {s.h1}
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 font-display text-xl md:text-2xl font-bold tracking-tight text-terracotta-deep">
                {s.accent}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-plum/85">
                {s.introA}{" "}
                <strong className="font-semibold text-ink">
                  {s.introStrong}
                </strong>{" "}
                {s.introB}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 pb-24 md:pb-32 space-y-8" data-testid="services-list">
        <div className="mx-auto max-w-7xl space-y-8">
          {offers.map((offer, i) => (
            <Reveal key={offer.slug} delay={0.05}>
              <article
                className={`group grid overflow-hidden rounded-[2rem] border border-ink/5 bg-white shadow-[0_20px_40px_-10px_rgba(17,14,13,0.05)] lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img
                    src={offer.image}
                    alt={offer.imageAlt}
                    loading="lazy"
                    className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${offer.imgClass || ""}`}
                  />
                  <span className="absolute top-5 left-5 rounded-full bg-cream/90 backdrop-blur px-4 py-1.5 text-xs font-semibold">
                    {t.common.offerBadge} {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex flex-col p-8 md:p-12">
                  <div className="flex flex-wrap gap-2 text-xs font-medium text-plum">
                    {offer.type && (
                      <span className="rounded-full bg-terracotta/15 px-3.5 py-1.5 font-semibold text-terracotta-deep">
                        {offer.type}
                      </span>
                    )}
                    <span className="rounded-full bg-sand px-3.5 py-1.5">{offer.duration}</span>
                    {offer.engagement && (
                      <span className="rounded-full bg-sand px-3.5 py-1.5">{offer.engagement}</span>
                    )}
                    <span className="rounded-full bg-sand px-3.5 py-1.5">{offer.price}</span>
                  </div>
                  <h2 className="mt-5 font-display text-2xl md:text-3xl font-bold tracking-tight">
                    {offer.name}
                  </h2>
                  <p className="mt-4 text-sm md:text-base font-semibold leading-relaxed text-ink">
                    {offer.accroche}
                  </p>
                  <p className="mt-2.5 text-sm md:text-base leading-relaxed text-plum/80">
                    {offer.description}
                  </p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {offer.keyPoints.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-plum/85">
                        <Check className="mt-0.5 w-4 h-4 shrink-0 text-terracotta-deep" strokeWidth={2.5} />
                        <span><Strong text={item} /></span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={l(`/services/${offer.frSlug || offer.slug}`)}
                    data-testid={`services-offer-${offer.frSlug || offer.slug}`}
                    className="group/link mt-8 inline-flex w-max items-center gap-2.5 rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-plum"
                  >
                    {t.common.discoverOffer}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="flex flex-col md:flex-row md:items-center gap-8 rounded-[2rem] bg-terracotta-light/40 p-8 md:p-12">
              <Sparkles className="w-10 h-10 shrink-0 text-terracotta-deep" strokeWidth={1.5} aria-hidden="true" />
              <p className="flex-1 text-base md:text-lg leading-relaxed text-plum/90">
                <strong className="font-semibold text-ink">{s.customStrong}</strong>{" "}
                {s.customText}
              </p>
              <a
                href={`mailto:${SITE.email}?subject=${encodeURIComponent(s.customSubject)}`}
                data-testid="services-custom-cta"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-terracotta px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-terracotta-dark"
              >
                {s.customCta}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABlock />
    </>
  );
}
